import { useCallback, useEffect } from "react";
import { useDispatch, useSelector } from "react-redux";

import {
  createCycle,
  fetchCyclesByUser,
  fetchPredictions,
  fetchFertilityWindow,
  fetchCycleSummary,
} from "../redux/thunks/cycleThunks";

import {
  selectCycles,
  selectPredictions,
  selectFertilityWindow,
  selectCycleSummary,
  selectCycleLoading,
  selectCycleError,
} from "../redux/selectors/cycleSelectors";

export const useUserCycle = (userId, options = {}) => {
  const dispatch = useDispatch();

  const {
    autoFetch = true,
    withPredictions = true,
    withFertilityWindow = true,
    withSummary = true,
  } = options;

  /* ---------------- STATE ---------------- */
  const cycles = useSelector(selectCycles);
  const predictions = useSelector(selectPredictions);
  const fertilityWindow = useSelector(selectFertilityWindow);
  const summary = useSelector(selectCycleSummary);
  const isLoading = useSelector(selectCycleLoading);
  const error = useSelector(selectCycleError);

  /* ---------------- LOADERS ---------------- */
  const loadCycles = useCallback(() => {
    if (!userId) return;
    dispatch(fetchCyclesByUser(userId));
  }, [dispatch, userId]);

  const loadPredictions = useCallback(() => {
    if (!userId) return;
    dispatch(fetchPredictions(userId));
  }, [dispatch, userId]);

  const loadFertilityWindow = useCallback(() => {
    if (!userId) return;
    dispatch(fetchFertilityWindow(userId));
  }, [dispatch, userId]);

  const loadSummary = useCallback(() => {
    if (!userId) return;
    dispatch(fetchCycleSummary(userId));
  }, [dispatch, userId]);

  /* ---------------- CREATE ---------------- */
  const addCycle = useCallback(
    async (payload) => {
      if (!userId) throw new Error("User non connecté");
      return dispatch(
        createCycle({
          userId,
          ...payload,
        })
      ).unwrap();
    },
    [dispatch, userId]
  );

  /* ---------------- AUTO FETCH ---------------- */
  useEffect(() => {
    if (!autoFetch || !userId) return;

    loadCycles();
    if (withPredictions) loadPredictions();
    if (withFertilityWindow) loadFertilityWindow();
    if (withSummary) loadSummary();
  }, [
    autoFetch,
    userId,
    withPredictions,
    withFertilityWindow,
    withSummary,
    loadCycles,
    loadPredictions,
    loadFertilityWindow,
    loadSummary,
  ]);

  return {
    cycles,
    predictions,
    fertilityWindow,
    summary,
    isLoading,
    error,

    reloadAll: () => {
      loadCycles();
      loadPredictions();
      loadFertilityWindow();
      loadSummary();
    },

    createCycle: addCycle,
  };
};
